import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  IconButton,
  InputAdornment,
  FormControl,
  FormLabel,
  Snackbar,
  Alert, Dialog, DialogActions, DialogContent, DialogTitle
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import { hosturl } from '../libs/Constant';
 

const CreateCurrent = () => {
  // Retrieve token from localStorage
  const token = localStorage.getItem('token');

  // State to manage form data
  const [formData, setFormData] = useState({
    fileNumber: '',
    receivedDate: dayjs(), // Initialize with current dayjs object
    memoNumber: '',
    officeNumber: '',
    receiveFrom: '',
    applicantName: '',
    subject: '',
    section: '',
    applicantPhoneNumber: [{ name: '', number: '' }],
    applicantMail: '',
    managementPhoneNumber: [{ name: '', number: '' }],
    managementMail: '',
    remarks: [{ date: dayjs(), text: '' }], // Initialize with current dayjs object
    url: '',
    applicantAddress: '',
    managementAddress: '',
    nextDateOfHearing: null, // Initialize as null
    finalStatus: '',
    amount: ''
  });

  // State for loading, error, and success messages
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // States for dynamic dropdown options
  const [receiveFromOptions, setReceiveFromOptions] = useState([]);
  const [sectionOptions, setSectionOptions] = useState([]);

  // States for dialogs to add new "Received From" and "Section" entries
  const [openReceiveFromDialog, setOpenReceiveFromDialog] = useState(false);
  const [openSectionDialog, setOpenSectionDialog] = useState(false);
  const [newEntryName, setNewEntryName] = useState(''); // Used for both dialogs

  // Helper function to show snackbar messages
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  // Handlers for opening/closing "Received From" dialog
  const handleOpenReceiveFromDialog = () => {
    setNewEntryName(''); // Clear previous input
    setOpenReceiveFromDialog(true);
  };

  const handleCloseReceiveFromDialog = () => {
    setOpenReceiveFromDialog(false);
  };

  // Handlers for opening/closing "Section" dialog
  const handleOpenSectionDialog = () => {
    setNewEntryName(''); // Clear previous input
    setOpenSectionDialog(true);
  };

  const handleCloseSectionDialog = () => {
    setOpenSectionDialog(false);
  };

  // Handles submission for adding a new "Received From" entry
  const handleSubmitReceiveFrom = async () => {
    try {
      const response = await fetch(`${hosturl}/receive-from`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newEntryName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.displayMessage || 'Failed to create entry');
      }

      showSnackbar('Received From created successfully', 'success');
      fetchReceiveFromEntries(); // Refresh options after creation
      handleCloseReceiveFromDialog();
    } catch (err) {
      showSnackbar(err.message, 'error');
    }
  };

  // Handles submission for adding a new "Section" entry
  const handleSubmitSection = async () => {
    try {
      const response = await fetch(`${hosturl}/section`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: newEntryName }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.displayMessage || 'Failed to create section');
      }

      showSnackbar('Section created successfully', 'success');
      fetchSectionEntries(); // Refresh options after creation
      handleCloseSectionDialog();
    } catch (err) {
      showSnackbar(err.message, 'error');
    }
  };

  // Generic handler for text input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handler for DatePicker changes
  const handleDateChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handlers for dynamic phone number fields (applicant and management)
  const handlePhoneNumberChange = (index, field, value, type) => {
    const updatedArray = [...formData[type]];
    updatedArray[index][field] = value;
    setFormData(prev => ({
      ...prev,
      [type]: updatedArray
    }));
  };

  const handleAddPhoneNumber = (type) => {
    setFormData(prev => ({
      ...prev,
      [type]: [...prev[type], { name: '', number: '' }]
    }));
  };

  const handleRemovePhoneNumber = (index, type) => {
    const updatedArray = [...formData[type]];
    updatedArray.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      [type]: updatedArray
    }));
  };

  // Handlers for dynamic remarks fields
  const handleRemarkChange = (index, field, value) => {
    const updatedRemarks = [...formData.remarks];
    if (field === 'date') {
      updatedRemarks[index].date = value;
    } else {
      updatedRemarks[index].text = value;
    }
    setFormData(prev => ({
      ...prev,
      remarks: updatedRemarks
    }));
  };

  const handleAddRemark = () => {
    setFormData(prev => ({
      ...prev,
      remarks: [...prev.remarks, { date: dayjs(), text: '' }] // Initialize with current dayjs object
    }));
  };

  const handleRemoveRemark = (index) => {
    const updatedRemarks = [...formData.remarks];
    updatedRemarks.splice(index, 1);
    setFormData(prev => ({
      ...prev,
      remarks: updatedRemarks
    }));
  };

  // Fetches "Received From" entries from the API
  const fetchReceiveFromEntries = async () => {
    try {
      const response = await fetch(`${hosturl}/receive-from`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setReceiveFromOptions(data.result || []);
    } catch (err) {
      console.error('Receive From fetch error:', err);
      showSnackbar('Failed to load "Received From" options.', 'error');
    }
  };

  // Fetches "Section" entries from the API
  const fetchSectionEntries = async () => {
    try {
      const response = await fetch(`${hosturl}/section`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setSectionOptions(data.result || []);
    } catch (err) {
      console.error('Section fetch error:', err);
      showSnackbar('Failed to load "Section" options.', 'error');
    }
  };

  // Effect to fetch initial dropdown options on component mount
  useEffect(() => {
    fetchReceiveFromEntries();
    fetchSectionEntries();
  }, []);

  // Handles the main form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!token) {
        showSnackbar('No access token found. Please log in.', 'error');
        return;
      }

      // Prepare payload, formatting dates correctly to YYYY-MM-DD local string
      const payload = {
        ...formData,
        receivedDate: formData.receivedDate ? formData.receivedDate.format('YYYY-MM-DD') : null,
        nextDateOfHearing: formData.nextDateOfHearing ? formData.nextDateOfHearing.format('YYYY-MM-DD') : null,
        remarks: formData.remarks.map((remark) => ({
          ...remark,
          date: remark.date ? remark.date.format('YYYY-MM-DD') : null,
        })),
      };

      const response = await fetch(`${hosturl}/form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        const errorMsg = data.displayMessage || 'Failed to create form';
        showSnackbar(errorMsg, 'error');
        return;
      }

      showSnackbar('Form created successfully!', 'success');

      // Reset form data after successful submission
      setFormData({
        fileNumber: '',
        receivedDate: dayjs(),
        memoNumber: '',
        officeNumber: '',
        receiveFrom: '',
        applicantName: '',
        subject: '',
        section: '',
        applicantPhoneNumber: [{ name: '', number: '' }],
        applicantMail: '',
        managementPhoneNumber: [{ name: '', number: '' }],
        managementMail: '',
        remarks: [{ date: dayjs(), text: '' }],
        url: '',
        applicantAddress: '',
        managementAddress: '',
        nextDateOfHearing: null,
        finalStatus: '',
        amount: '',
      });
    } catch (err) {
      showSnackbar(err.message || 'An error occurred while submitting the form', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Options for Final Status dropdown
  const statusOptions = [
    'Open',
    'Forwarded',
    'Closed',
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, mt: 4, borderRadius: '12px' }}>
          <Typography variant="h5" component="h1" gutterBottom align="center" style={{ fontFamily: "Poppins", fontWeight: 600, color: '#333' }}>
            Create Current Entry
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ color: '#555' }}>
                  Basic Information
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="File Number"
                  name="fileNumber"
                  value={formData.fileNumber}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ borderRadius: '8px' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Received Date"
                  value={formData.receivedDate}
                  onChange={(date) => handleDateChange('receivedDate', date)}
                  format="DD/MM/YYYY" // Display format
                  slotProps={{ textField: { fullWidth: true, variant: 'outlined', sx: { borderRadius: '8px' } } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Memo Number"
                  name="memoNumber"
                  value={formData.memoNumber}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ borderRadius: '8px' }}
                />
              </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ borderRadius: '8px' }}
                    />
                  </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    select
                    label="Received From"
                    name="receiveFrom"
                    value={formData.receiveFrom}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ borderRadius: '8px' }}
                  >
                    {receiveFromOptions.map((option) => (
                      <MenuItem key={option._id} value={option.name}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    color="primary"
                    onClick={handleOpenReceiveFromDialog}
                    sx={{ ml: 1, p: 1.5, borderRadius: '50%', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                    aria-label="Add new Received From"
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                <Box display="flex" alignItems="center">
                  <TextField
                    fullWidth
                    select
                    label="Section"
                    name="section"
                    value={formData.section}
                    onChange={handleChange}
                    variant="outlined"
                    sx={{ borderRadius: '8px' }}
                  >
                    {sectionOptions.map((option) => (
                      <MenuItem key={option._id} value={option.name}>
                        {option.name}
                      </MenuItem>
                    ))}
                  </TextField>
                  <IconButton
                    color="primary"
                    onClick={handleOpenSectionDialog}
                    sx={{ ml: 1, p: 1.5, borderRadius: '50%', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}
                    aria-label="Add new Section"
                  >
                    <AddCircleOutlineIcon />
                  </IconButton>
                  
                </Box>
                
              </Grid>

              {/* Conditional rendering for JM section fields */}
              {formData.section === 'JM' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2, color: '#555' }}>
                      Applicant Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Applicant Name"
                      name="applicantName"
                      value={formData.applicantName}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ borderRadius: '8px' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <FormLabel sx={{ mb: 1, color: '#333' }}>Applicant Phone Numbers</FormLabel>
                      {formData.applicantPhoneNumber.map((phone, index) => (
                        <Grid container spacing={2} key={index} alignItems="center" sx={{ mt: 0.5 }}>
                          <Grid item xs={5}>
                            <TextField
                              fullWidth
                              label="Name"
                              value={phone.name}
                              onChange={(e) => handlePhoneNumberChange(index, 'name', e.target.value, 'applicantPhoneNumber')}
                              variant="outlined"
                              sx={{ borderRadius: '8px' }}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              value={phone.number}
                              onChange={(e) => handlePhoneNumberChange(index, 'number', e.target.value, 'applicantPhoneNumber')}
                              variant="outlined"
                              sx={{ borderRadius: '8px' }}
                            />
                          </Grid>
                          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton onClick={() => handleRemovePhoneNumber(index, 'applicantPhoneNumber')} color="error" aria-label="Remove phone number">
                              <RemoveCircleOutlineIcon />
                            </IconButton>
                            {index === formData.applicantPhoneNumber.length - 1 && (
                              <IconButton onClick={() => handleAddPhoneNumber('applicantPhoneNumber')} color="primary" aria-label="Add phone number">
                                <AddCircleOutlineIcon />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      ))}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Applicant Email"
                      name="applicantMail"
                      type="email"
                      value={formData.applicantMail}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ borderRadius: '8px' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Applicant Address"
                      name="applicantAddress"
                      value={formData.applicantAddress}
                      onChange={handleChange}
                      variant="outlined"
                      multiline
                      rows={2}
                      sx={{ borderRadius: '8px' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2, color: '#555' }}>
                      Management Information
                    </Typography>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Management Name"
                      name="managementName"
                      value={formData.managementName}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ borderRadius: '8px' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={{ mt: 1 }}>
                      <FormLabel sx={{ mb: 1, color: '#333' }}>Management Phone Numbers</FormLabel>
                      {formData.managementPhoneNumber.map((phone, index) => (
                        <Grid container spacing={2} key={index} alignItems="center" sx={{ mt: 0.5 }}>
                          <Grid item xs={5}>
                            <TextField
                              fullWidth
                              label="Name"
                              value={phone.name}
                              onChange={(e) => handlePhoneNumberChange(index, 'name', e.target.value, 'managementPhoneNumber')}
                              variant="outlined"
                              sx={{ borderRadius: '8px' }}
                            />
                          </Grid>
                          <Grid item xs={5}>
                            <TextField
                              fullWidth
                              label="Phone Number"
                              value={phone.number}
                              onChange={(e) => handlePhoneNumberChange(index, 'number', e.target.value, 'managementPhoneNumber')}
                              variant="outlined"
                              sx={{ borderRadius: '8px' }}
                            />
                          </Grid>
                          <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <IconButton onClick={() => handleRemovePhoneNumber(index, 'managementPhoneNumber')} color="error" aria-label="Remove phone number">
                              <RemoveCircleOutlineIcon />
                            </IconButton>
                            {index === formData.managementPhoneNumber.length - 1 && (
                              <IconButton onClick={() => handleAddPhoneNumber('managementPhoneNumber')} color="primary" aria-label="Add phone number">
                                <AddCircleOutlineIcon />
                              </IconButton>
                            )}
                          </Grid>
                        </Grid>
                      ))}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Management Email"
                      name="managementMail"
                      type="email"
                      value={formData.managementMail}
                      onChange={handleChange}
                      variant="outlined"
                      sx={{ borderRadius: '8px' }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Management Address"
                      name="managementAddress"
                      value={formData.managementAddress}
                      onChange={handleChange}
                      variant="outlined"
                      multiline
                      rows={2}
                      sx={{ borderRadius: '8px' }}
                    />
                  </Grid>
                </>
              )}

              {/* Additional Information */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom sx={{ mt: 2, color: '#555' }}>
                  Additional Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <FormLabel sx={{ mb: 1, color: '#333' }}>Remarks</FormLabel>
                  {formData.remarks?.map((remark, index) => (
                    <Grid container spacing={2} key={index} alignItems="center" sx={{ mt: 0.5 }}>
                      <Grid item xs={4}>
                        <DatePicker
                          label="Date"
                          value={remark.date}
                          onChange={(date) => handleRemarkChange(index, 'date', date)}
                          format="DD/MM/YYYY" // Display format
                          slotProps={{ textField: { fullWidth: true, variant: 'outlined', sx: { borderRadius: '8px' } } }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label="Remark Text"
                          value={remark.text}
                          onChange={(e) => handleRemarkChange(index, 'text', e.target.value)}
                          variant="outlined"
                          multiline
                          rows={1}
                          sx={{ borderRadius: '8px' }}
                        />
                      </Grid>
                      <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <IconButton onClick={() => handleRemoveRemark(index)} color="error" aria-label="Remove remark">
                          <RemoveCircleOutlineIcon />
                        </IconButton>
                        {index === formData.remarks.length - 1 && (
                          <IconButton onClick={handleAddRemark} color="primary" aria-label="Add remark">
                            <AddCircleOutlineIcon />
                          </IconButton>
                        )}
                      </Grid>
                    </Grid>
                  ))}
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="URL"
                  name="url"
                  value={formData.url}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ borderRadius: '8px' }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <DatePicker
                  label="Next Date of Hearing"
                  value={formData.nextDateOfHearing}
                  onChange={(date) => handleDateChange('nextDateOfHearing', date)}
                  format="DD/MM/YYYY" // Display format
                  slotProps={{ textField: { fullWidth: true, variant: 'outlined', sx: { borderRadius: '8px' } } }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Final Status"
                  name="finalStatus"
                  value={formData.finalStatus}
                  onChange={handleChange}
                  variant="outlined"
                  sx={{ borderRadius: '8px' }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  type="number"
                  variant="outlined"
                  InputProps={{
                    startAdornment: <InputAdornment position="start">₹</InputAdornment>,
                  }}
                  sx={{ borderRadius: '8px' }}
                />
              </Grid>

              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2, py: 1.5, borderRadius: '8px', backgroundColor: '#1976d2', '&:hover': { backgroundColor: '#115293' } }}
                >
                  {loading ? 'Submitting...' : 'Create Form'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Paper>

        {/* Snackbar for success/error messages */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            sx={{ width: '100%', borderRadius: '8px' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>

        {/* Dialog for adding new "Received From" entry */}
        <Dialog open={openReceiveFromDialog} onClose={handleCloseReceiveFromDialog} PaperProps={{ sx: { borderRadius: '12px' } }}>
          <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>Add Received From</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              fullWidth
              value={newEntryName}
              onChange={(e) => setNewEntryName(e.target.value)}
              variant="outlined"
              sx={{ borderRadius: '8px' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseReceiveFromDialog} color="secondary" sx={{ borderRadius: '8px' }}>Cancel</Button>
            <Button onClick={handleSubmitReceiveFrom} variant="contained" color="primary" sx={{ borderRadius: '8px' }}>Add</Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for adding new "Section" entry */}
        <Dialog open={openSectionDialog} onClose={handleCloseSectionDialog} PaperProps={{ sx: { borderRadius: '12px' } }}>
          <DialogTitle sx={{ fontWeight: 600, color: '#333' }}>Add Section</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              fullWidth
              value={newEntryName}
              onChange={(e) => setNewEntryName(e.target.value)}
              variant="outlined"
              sx={{ borderRadius: '8px' }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseSectionDialog} color="secondary" sx={{ borderRadius: '8px' }}>Cancel</Button>
            <Button onClick={handleSubmitSection} variant="contained" color="primary" sx={{ borderRadius: '8px' }}>Add</Button>
          </DialogActions>
        </Dialog>

      </Container>
    </LocalizationProvider>
  );
};

export default CreateCurrent;
